import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, MenuItem, Skeleton, Typography } from "@mui/material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AddIcon from "@mui/icons-material/Add";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdSectionCard,
  IpdTextField,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { listVitals, recordVitals } from "../api/emergencyApi";

/**
 * Vital signs recorded during the visit (ER-TRI-005/006).
 *
 * Taken at first contact and again through the visit, newest first, each with
 * who took them and when. A reading outside its normal range is flagged the
 * moment it is typed and stays flagged in the saved list — the same computation
 * from the same fixed ranges, so entry and later reads never disagree
 * (ER-TRI-006). Nothing about the flag is stored; the value is stored, in range
 * or not.
 *
 * Ranges below are adult normal ranges — a clinical prompt, never a hard rule
 * (an out-of-range value still saves). Paediatric ranges differ and are a
 * follow-up if the department needs them.
 *
 * Fetches its own list, so it carries a real loading state and refreshes after
 * a set without reloading the whole visit — the same shape as the medicines
 * card.
 */

const CONSCIOUSNESS = ["Alert", "Verbal", "Pain", "Unresponsive"];

const FIELDS = [
  { key: "bp_systolic", label: "Systolic", unit: "mmHg", low: 90, high: 140 },
  { key: "bp_diastolic", label: "Diastolic", unit: "mmHg", low: 60, high: 90 },
  { key: "heart_rate", label: "Heart rate", unit: "bpm", low: 60, high: 100 },
  { key: "respiratory_rate", label: "Resp. rate", unit: "/min", low: 12, high: 20 },
  { key: "temperature", label: "Temp.", unit: "°C", low: 36.1, high: 37.5, decimal: true },
  { key: "spo2", label: "SpO₂", unit: "%", low: 95, high: 100 },
  { key: "weight_kg", label: "Weight", unit: "kg", decimal: true },
];
const FIELD_BY_KEY = Object.fromEntries(FIELDS.map((f) => [f.key, f]));
const VITAL_KEYS = [...FIELDS.map((f) => f.key), "consciousness"];

function outOfRange(key, value) {
  if (value == null || value === "") return false;
  if (key === "consciousness") return value !== "Alert";
  const f = FIELD_BY_KEY[key];
  if (!f || f.low == null) return false;
  const n = Number(value);
  if (Number.isNaN(n)) return false;
  return n < f.low || n > f.high;
}

function rangeHint(key, value) {
  if (!outOfRange(key, value)) return "";
  if (key === "consciousness") return "not alert";
  return Number(value) < FIELD_BY_KEY[key].low ? "below normal" : "above normal";
}

/** Drop trailing zeros a DECIMAL column adds ("37.40" -> "37.4"). */
function fmt(key, value) {
  const f = FIELD_BY_KEY[key];
  if (f && f.decimal && value != null && value !== "") {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : String(n);
  }
  return String(value);
}

/** The readings of a saved set, in display order, each flagged in or out of range. */
function readings(v) {
  const items = [];
  if (v.bp_systolic != null || v.bp_diastolic != null) {
    items.push({
      label: "BP",
      text: `${v.bp_systolic != null ? v.bp_systolic : "–"}/${v.bp_diastolic != null ? v.bp_diastolic : "–"}`,
      unit: "mmHg",
      oor: outOfRange("bp_systolic", v.bp_systolic) || outOfRange("bp_diastolic", v.bp_diastolic),
    });
  }
  [
    ["heart_rate", "HR"],
    ["respiratory_rate", "RR"],
    ["temperature", "Temp"],
    ["spo2", "SpO₂"],
    ["weight_kg", "Weight"],
  ].forEach(([key, label]) => {
    if (v[key] != null && v[key] !== "") {
      items.push({ label, text: fmt(key, v[key]), unit: FIELD_BY_KEY[key].unit, oor: outOfRange(key, v[key]) });
    }
  });
  if (v.consciousness) {
    items.push({ label: "AVPU", text: v.consciousness, unit: "", oor: outOfRange("consciousness", v.consciousness) });
  }
  return items;
}

const blankForm = () =>
  [...VITAL_KEYS, "notes"].reduce((acc, k) => ({ ...acc, [k]: "" }), {});

export default function VitalsCard({ visit, disabled, onRecorded }) {
  const visitId = visit.id;

  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSets(await listVitals(visitId));
      setLoadError("");
    } catch (e) {
      setLoadError(e?.message || "Could not load the vitals");
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));
  const ready = VITAL_KEYS.some((k) => String(form[k]).trim() !== "");

  const submit = async () => {
    setBusy(true);
    setFormError("");
    try {
      const payload = {};
      [...VITAL_KEYS, "notes"].forEach((k) => {
        const val = String(form[k]).trim();
        if (val !== "") payload[k] = val;
      });
      const { message } = await recordVitals(visitId, payload);
      setForm(blankForm());
      setAdding(false);
      await load();
      onRecorded?.(message);
    } catch (e) {
      setFormError(e?.message || "Could not record the vitals");
    } finally {
      setBusy(false);
    }
  };

  return (
    <IpdSectionCard
      title="Vitals"
      subtitle="Taken at first contact and through the visit — out-of-range readings flagged"
      actions={
        !disabled &&
        !adding && (
          <IpdButton
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setForm(blankForm());
              setFormError("");
              setAdding(true);
            }}
          >
            Record vitals
          </IpdButton>
        )
      }
    >
      {/* ---------------------------------------------------------- the form */}
      {adding && (
        <Box
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
            border: `1px solid ${IPD_COLORS.border}`,
            bgcolor: IPD_COLORS.cardBg,
          }}
        >
          {formError && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
              onClose={() => setFormError("")}
            >
              {formError}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            {FIELDS.map((f) => {
              const oor = outOfRange(f.key, form[f.key]);
              return (
                <Box key={f.key}>
                  <Typography sx={{ ...ipdLabelSx, mb: 0.5 }}>
                    {f.label}
                    {f.unit ? ` (${f.unit})` : ""}
                  </Typography>
                  <IpdTextField
                    type="number"
                    value={form[f.key]}
                    onChange={(e) => setField({ [f.key]: e.target.value })}
                    size="small"
                    fullWidth
                    error={oor}
                    helperText={oor ? `Outside normal range — ${rangeHint(f.key, form[f.key])}` : ""}
                    sx={ipdElevatedInputSx}
                    inputProps={{ "aria-label": f.label, inputMode: "decimal" }}
                  />
                </Box>
              );
            })}

            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.5 }}>Consciousness</Typography>
              <IpdTextField
                select
                value={form.consciousness}
                onChange={(e) => setField({ consciousness: e.target.value })}
                size="small"
                fullWidth
                error={outOfRange("consciousness", form.consciousness)}
                helperText={
                  outOfRange("consciousness", form.consciousness) ? "Not alert" : ""
                }
                SelectProps={{ MenuProps: getIpdSelectMenuProps() }}
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Consciousness" }}
              >
                <MenuItem value="">—</MenuItem>
                {CONSCIOUSNESS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </IpdTextField>
            </Box>

            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <Typography sx={{ ...ipdLabelSx, mb: 0.5 }}>Notes (optional)</Typography>
              <IpdTextField
                value={form.notes}
                onChange={(e) => setField({ notes: e.target.value })}
                placeholder="Anything worth recording with this set"
                size="small"
                fullWidth
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Notes" }}
              />
            </Box>
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.75 }}
          >
            <IpdButton
              variant="outlined"
              onClick={() => {
                setAdding(false);
                setFormError("");
              }}
              disabled={busy}
            >
              Cancel
            </IpdButton>
            <IpdButton onClick={submit} loading={busy} disabled={!ready}>
              Record vitals
            </IpdButton>
          </Box>
        </Box>
      )}

      {/* ---------------------------------------------------------- the list */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[0, 1].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={56}
              sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}
            />
          ))}
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          action={
            <IpdButton variant="outlined" size="small" onClick={load}>
              Retry
            </IpdButton>
          }
        >
          {loadError}
        </Alert>
      ) : sets.length === 0 ? (
        !adding && (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              px: 2,
              border: `1px dashed ${IPD_COLORS.borderHover}`,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
            }}
          >
            <MonitorHeartIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
              No vitals recorded yet
            </Typography>
            <Typography
              sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}
            >
              {disabled
                ? "No vitals were recorded on this visit."
                : "Record the first set of observations — anything outside its normal range is flagged as you type."}
            </Typography>
          </Box>
        )
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {sets.map((v) => {
            const items = readings(v);
            const anyOor = items.some((r) => r.oor);
            return (
              <Box
                key={v.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: `${IPD_SHAPE.radiusMd}px`,
                  border: `1px solid ${anyOor ? IPD_COLORS.error.text : IPD_COLORS.border}`,
                  bgcolor: anyOor ? IPD_COLORS.error.bg : IPD_COLORS.cardBg,
                }}
              >
                <MonitorHeartIcon
                  sx={{
                    fontSize: 18,
                    mt: 0.2,
                    color: anyOor ? IPD_COLORS.error.text : IPD_COLORS.primary,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.25,
                      alignItems: "baseline",
                    }}
                  >
                    {items.map((r, i) => (
                      <Box
                        key={i}
                        component="span"
                        sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.4 }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: IPD_COLORS.textMuted,
                            textTransform: "uppercase",
                            letterSpacing: 0.3,
                          }}
                        >
                          {r.label}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 13.5,
                            fontWeight: r.oor ? 800 : 600,
                            color: r.oor ? IPD_COLORS.error.text : IPD_COLORS.textPrimary,
                          }}
                        >
                          {r.text}
                          {r.unit ? (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 10,
                                ml: 0.2,
                                color: r.oor ? IPD_COLORS.error.text : IPD_COLORS.textMuted,
                              }}
                            >
                              {r.unit}
                            </Typography>
                          ) : null}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography
                    sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted, mt: 0.4 }}
                  >
                    {formatDateTime(v.recorded_at)}
                    {v.recorded_by_name ? ` · ${v.recorded_by_name}` : ""}
                  </Typography>
                  {v.notes && (
                    <Typography
                      sx={{ fontSize: 12.5, color: IPD_COLORS.textPrimary, mt: 0.3 }}
                    >
                      {v.notes}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </IpdSectionCard>
  );
}
